'use client';

import { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  limit,
  DocumentData,
  FirestoreError,
  type QuerySnapshot,
} from 'firebase/firestore';
import { useFirestore } from '@/firebase/provider';
import { useDebounce } from '@/hooks/use-debounce';
import { onSnapshotFromServer } from './server-first-snapshot';

/**
 * The users document behind a username, live.
 *
 * `initialData` is the server's own read of that user (the storefront passes a
 * projection of it; see src/lib/storefront-seed.ts). With it the hook starts
 * with that data and not loading, so the first render is the real page, and the
 * listener waits for the server rather than this browser's cache before it
 * replaces it (onSnapshotFromServer). It is read once, on mount, and only for
 * the username it was built for.
 *
 * `isLive` says the data in hand came from the listener, not from
 * `initialData`: a caller handed a projection uses it to hold back anything
 * that reads a field the projection leaves out.
 */
export function useUserByUsername(
  username: string,
  options?: { initialData?: DocumentData | null },
) {
  const [seed] = useState(() => {
    const initial = options?.initialData;
    return initial && initial.username === username ? initial : null;
  });
  const [data, setData] = useState<DocumentData | null>(seed);
  const [isLoading, setIsLoading] = useState(seed === null);
  const [isLive, setIsLive] = useState(false);
  const [error, setError] = useState<FirestoreError | null>(null);
  const firestore = useFirestore();
  const debouncedUsername = useDebounce(username, 300);

  // When the username prop changes, immediately go into a loading state and
  // clear old data. This prevents showing stale data or a "deleted" state for
  // a flash before the new data is fetched. Not on mount: there is nothing old
  // to clear then, and clearing would throw away `initialData`.
  const resetForRef = useRef(username);
  useEffect(() => {
    if (resetForRef.current === username) return;
    resetForRef.current = username;
    setIsLoading(true);
    setIsLive(false);
    setData(null);
  }, [username]);

  useEffect(() => {
    if (!debouncedUsername || !firestore) {
      setIsLoading(false);
      return;
    }

    // The loading state is already set by the effect above.
    setError(null);

    const usersRef = collection(firestore, 'users');
    const q = query(usersRef, where('username', '==', debouncedUsername), limit(1));

    // Live listener rather than a one-shot read: a profile that's open while the
    // owner edits it (avatar, banner, notes, tier) repaints instead of going
    // stale, which is what makes the owner's own /profile/<name> page a truthful
    // preview of what visitors see.
    const onNext = (querySnapshot: QuerySnapshot) => {
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        setData({ id: userDoc.id, ...userDoc.data() });
      } else {
        setData(null);
      }
      setIsLive(true);
      setIsLoading(false);
    };
    const onError = (err: FirestoreError) => {
      setError(err);
      setIsLoading(false);
    };
    const seeded = seed !== null && seed.username === debouncedUsername;
    const unsubscribe = seeded ? onSnapshotFromServer(q, onNext, onError) : onSnapshot(q, onNext, onError);

    return () => unsubscribe();
    // `seed` is state that never changes after mount.
  }, [debouncedUsername, firestore]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, isLoading, error, isLive };
}
